import ExcelJS from "exceljs";
import { SUBCONTRACT_ITEM_IMPORT_COLUMNS } from "./validateSubcontractItemsImport";

export const SUBCONTRACT_IMPORT_IGNORED_SHEETS = [
  "Danh_muc_co_dinh",
  "Phien_ban",
  "Tổng hợp",
];

// Chuẩn hóa tiêu đề/sheet để so khớp không phụ thuộc dấu và khoảng trắng.
export const normalizeSubcontractImportLookup = (value) =>
  (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[_\s]+/g, " ");

// Lấy giá trị thực của ô Excel, bao gồm công thức, hyperlink và rich text.
const getCellValue = (cell) => {
  const value = cell?.value;
  if (value && typeof value === "object") {
    if (value.result !== undefined) return value.result;
    if (value.text !== undefined) return value.text;
    if (value.hyperlink && value.text) return value.text;
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join("");
    }
  }
  return value;
};

export const isIgnoredSubcontractImportSheet = (sheetName) => {
  const normalizedName = normalizeSubcontractImportLookup(sheetName);
  return SUBCONTRACT_IMPORT_IGNORED_SHEETS.some(
    (ignoredName) => normalizeSubcontractImportLookup(ignoredName) === normalizedName,
  );
};

const findHeaderRow = (worksheet, expectedHeaders) => {
  let headerRow = null;
  const columnIndexByKey = {};

  worksheet.eachRow((row, rowNumber) => {
    if (headerRow) return;

    const normalizedCells = [];
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      normalizedCells[colNumber] = normalizeSubcontractImportLookup(getCellValue(cell));
    });

    const hasImportCode = expectedHeaders
      .find((column) => column.key === "importCode")
      ?.normalizedHeaders.some((header) => normalizedCells.includes(header));
    const hasName = expectedHeaders
      .find((column) => column.key === "itemName")
      ?.normalizedHeaders.some((header) => normalizedCells.includes(header));

    if (!hasImportCode || !hasName) return;

    headerRow = rowNumber;
    expectedHeaders.forEach((column) => {
      const index = normalizedCells.findIndex((value) =>
        column.normalizedHeaders.includes(value),
      );
      if (index > -1) columnIndexByKey[column.key] = index;
    });
  });

  return { headerRow, columnIndexByKey };
};

const readWorksheetRows = (worksheet, expectedHeaders) => {
  const { headerRow, columnIndexByKey } = findHeaderRow(worksheet, expectedHeaders);
  if (!headerRow) {
    return {
      rows: [],
      error: `Sheet "${worksheet.name}" không tìm thấy dòng tiêu đề có cột STT import và Dự toán`,
    };
  }

  const rows = [];
  for (let rowNumber = headerRow + 1; rowNumber <= worksheet.rowCount; rowNumber += 1) {
    const worksheetRow = worksheet.getRow(rowNumber);
    const rowData = { sheetName: worksheet.name, rowNumber };
    SUBCONTRACT_ITEM_IMPORT_COLUMNS.forEach((column) => {
      const colNumber = columnIndexByKey[column.key];
      rowData[column.key] = colNumber
        ? getCellValue(worksheetRow.getCell(colNumber))
        : "";
    });

    const hasValue = SUBCONTRACT_ITEM_IMPORT_COLUMNS.some((column) => {
      const value = rowData[column.key];
      return value !== null && value !== undefined && value.toString().trim() !== "";
    });
    if (hasValue) rows.push(rowData);
  }

  return { rows, error: null };
};

// Đọc tất cả sheet dữ liệu; mỗi sheet sẽ được ánh xạ vào đúng một scope khi validate.
export const readSubcontractItemsImportExcel = async (file) => {
  const workbook = new ExcelJS.Workbook();
  const buffer = await file.arrayBuffer();
  await workbook.xlsx.load(buffer);

  if (!workbook.worksheets.length) {
    throw new Error("File Excel không có worksheet hợp lệ");
  }

  const expectedHeaders = SUBCONTRACT_ITEM_IMPORT_COLUMNS.map((column) => ({
    ...column,
    normalizedHeaders: [column.header, ...(column.aliases || [])]
      .map(normalizeSubcontractImportLookup),
  }));
  const rows = [];
  const sheets = [];
  const ignoredSheets = [];
  const errors = [];

  workbook.worksheets.forEach((worksheet) => {
    if (isIgnoredSubcontractImportSheet(worksheet.name)) {
      ignoredSheets.push(worksheet.name);
      return;
    }

    sheets.push(worksheet.name);
    const result = readWorksheetRows(worksheet, expectedHeaders);
    rows.push(...result.rows);
    if (result.error) errors.push({ sheetName: worksheet.name, message: result.error });
  });

  if (!sheets.length) {
    throw new Error("File Excel không có sheet dự toán hợp lệ");
  }

  return { rows, sheets, ignoredSheets, errors };
};

export default readSubcontractItemsImportExcel;
